// Shared header/CTA pieces for service deep-dive pages.
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Calendar } from "lucide-react";

export function ServicePageHeader({ eyebrow, title, lead, breadcrumb }) {
  return (
    <section className="pt-28 pb-12 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(900px_400px_at_50%_-10%,rgba(0,229,255,0.10),transparent_60%)] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
        <Link to="/" className="text-sm text-[#94A3B8] hover:text-[#22D3EE] inline-flex items-center gap-2 mb-6">
          <ArrowLeft size={14}/> Back to home
        </Link>
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="eyebrow">{eyebrow}</div>
            <h1 className="mt-3 text-4xl md:text-6xl font-extrabold tracking-tighter text-white leading-[1.05]">{title}</h1>
            {breadcrumb && <p className="mt-3 text-xs uppercase tracking-[0.25em] text-[#64748B]">{breadcrumb}</p>}
          </div>
          <div className="lg:col-span-4">
            <p className="text-[#94A3B8] leading-relaxed">{lead}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

export function ServicePageCTA() {
  return (
    <section className="py-20 border-t border-white/5">
      <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
        <div className="eyebrow">Let's build together</div>
        <h2 className="mt-3 text-3xl md:text-5xl font-bold text-white tracking-tight">
          Have a project in mind?
        </h2>
        <p className="mt-4 text-[#94A3B8] max-w-2xl mx-auto">
          Tell us about your scope — we'll come back within one business day with a fit, a plan, and an estimate.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <a href="/#book" className="btn-primary"><Calendar size={16}/> Book a consultation</a>
          <a href="/contact" className="btn-secondary">Send a brief <ArrowRight size={14}/></a>
        </div>
      </div>
    </section>
  );
}

export function SubServiceCard({ image, title, body, tools, reverse, readMoreHref, internalHref }) {
  return (
    <div className={`grid lg:grid-cols-12 gap-10 items-center ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`}>
      <div className="lg:col-span-6">
        <div className="relative rounded-2xl overflow-hidden border border-white/10">
          <img src={image} alt={title} className="w-full h-[320px] object-cover" loading="lazy"/>
          <div className="absolute inset-0 bg-gradient-to-t from-[#08060F] via-transparent to-transparent"/>
        </div>
      </div>
      <div className="lg:col-span-6">
        <h3 className="text-2xl md:text-3xl font-bold text-white">{title}</h3>
        {body.map((p, i) => (
          <p key={i} className="mt-3 text-[#94A3B8] leading-relaxed">{p}</p>
        ))}
        {tools && tools.length > 0 && (
          <div className="mt-5">
            <div className="text-xs uppercase tracking-[0.2em] text-[#64748B] mb-2">Toolchain</div>
            <div className="flex flex-wrap gap-2">
              {tools.map((t) => (
                <span key={t} className="px-3 py-1 rounded-full text-xs border border-white/15 text-[#E2E8F0] bg-white/[0.03]">{t}</span>
              ))}
            </div>
          </div>
        )}
        <div className="mt-6 flex flex-wrap gap-3 items-center">
          {internalHref && (
            <Link to={internalHref} className="btn-primary !py-2 !px-4 text-sm">
              Read more <ArrowRight size={14}/>
            </Link>
          )}
          {readMoreHref && (
            <a href={readMoreHref} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-[#94A3B8] hover:text-[#22D3EE]">
              View on thantrick.org
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
